import React from 'react';
import styled from 'styled-components';
import { FiFolder, FiEdit2, FiTrash2, FiEye, FiShare2, FiUsers } from 'react-icons/fi';

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const ColorBar = styled.div`
  height: 6px;
  background: linear-gradient(135deg, ${props => props.color || '#0ea5e9'} 0%, ${props => props.color || '#0284c7'} 100%);
  width: 100%;
`;

const CardContent = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const IconContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${props => props.color || '#0ea5e9'} 0%, ${props => props.color || '#0284c7'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
`;

const TitleSection = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

const Description = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
`;

const Stats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: rgba(14, 165, 233, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(14, 165, 233, 0.1);
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #374151;
  font-weight: 500;
`;

const StatNumber = styled.span`
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 700;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  opacity: 1;
  transition: opacity 0.3s ease;
`;

const ActionButton = styled.button`
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  min-height: 32px;
  
  &:hover {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  &.view {
    color: #0ea5e9;
    border-color: rgba(14, 165, 233, 0.2);
    &:hover {
      background: rgba(14, 165, 233, 0.1);
    }
  }
  
  &.edit {
    color: #f59e0b;
    border-color: rgba(245, 158, 11, 0.2);
    &:hover {
      background: rgba(245, 158, 11, 0.1);
    }
  }
  
  &.delete {
    color: #ef4444;
    border-color: rgba(239, 68, 68, 0.2);
    &:hover {
      background: rgba(239, 68, 68, 0.1);
    }
  }
  
  &.share {
    color: #8b5cf6;
    border-color: rgba(139, 92, 246, 0.2);
    &:hover {
      background: rgba(139, 92, 246, 0.1);
    }
  }
`;

const FolderCard = ({ 
  folder, 
  onView, 
  onEdit, 
  onDelete, 
  onShare,
  onShareWithUsers,
  onClick 
}) => {
  return (
    <Card onClick={onClick}>
      <ColorBar color={folder.color} />
      <CardContent>
        <Header>
          <IconContainer color={folder.color}>
            <FiFolder />
          </IconContainer>
          <TitleSection>
            <Title>{folder.name}</Title>
            {folder.description && (
              <Description>{folder.description}</Description>
            )}
          </TitleSection>
        </Header>

        <Stats>
          <StatItem>
            <StatNumber>{folder.form_count || 0}</StatNumber>
            <span>forms</span>
          </StatItem>
          <StatItem>
            <span>Created</span>
            <span>{new Date(folder.created_at).toLocaleDateString()}</span>
          </StatItem>
        </Stats>

        <Actions>
          <ActionButton 
            className="view"
            onClick={(e) => {
              e.stopPropagation();
              onView(folder);
            }}
          >
            <FiEye size={14} />
            View
          </ActionButton>
          {/* Only show management actions for owned folders */}
          {folder.folder_type === 'owned' && (
            <>
              <ActionButton 
                className="share"
                onClick={(e) => {
                  e.stopPropagation();
                  onShare(folder);
                }}
              >
                <FiShare2 size={14} />
                Public
              </ActionButton>
              <ActionButton 
                className="share"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onShareWithUsers) onShareWithUsers(folder);
                }}
              >
                <FiUsers size={14} />
                Users
              </ActionButton>
              <ActionButton 
                className="edit"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onEdit) onEdit(folder);
                }}
              >
                <FiEdit2 size={14} />
                Edit
              </ActionButton>
              <ActionButton 
                className="delete"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDelete) onDelete(folder);
                }}
              >
                <FiTrash2 size={14} />
                Delete
              </ActionButton>
            </>
          )}
          {/* Show a badge for shared folders */}
          {folder.folder_type === 'shared' && (
            <span style={{
              fontSize: '10px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              fontWeight: '500',
              marginLeft: '8px'
            }}>
              Shared
            </span>
          )}
        </Actions>
      </CardContent>
    </Card>
  );
};

export default FolderCard;
